/** @format */

import axios from 'src/utils/axios';
import { API_URL, ENDPOINTS } from 'src/constants';
import type { AcademyTrainingDetail } from 'src/types';

const CACHE_KEY = 'academyTrainingPreviewImages';
const CACHE_TTL_MS = 60 * 60 * 1000;

interface TrainingImageCacheEntry {
  imageUrl: string;
  expiresAt: number;
}

type TrainingImageCache = Record<string, TrainingImageCacheEntry>;

const resolveImageUrl = (image?: string | null): string | null => {
  if (!image) return null;
  if (image.startsWith('http://') || image.startsWith('https://')) return image;
  return `${API_URL}${image}`;
};

const readCache = (): TrainingImageCache => {
  try {
    const raw = window.localStorage.getItem(CACHE_KEY);
    return raw ? (JSON.parse(raw) as TrainingImageCache) : {};
  } catch {
    return {};
  }
};

const writeCache = (cache: TrainingImageCache): void => {
  try {
    window.localStorage.setItem(CACHE_KEY, JSON.stringify(cache));
  } catch {
    // Ignore localStorage quota or access errors.
  }
};

const pruneExpiredEntries = (cache: TrainingImageCache): TrainingImageCache => {
  const now = Date.now();
  return Object.entries(cache).reduce<TrainingImageCache>((pruned, [trainingId, entry]) => {
    if (entry?.imageUrl && entry.expiresAt > now) {
      pruned[trainingId] = entry;
    }
    return pruned;
  }, {});
};

const getValidCache = () => {
  const cache = readCache();
  const pruned = pruneExpiredEntries(cache);
  if (Object.keys(pruned).length !== Object.keys(cache).length) {
    writeCache(pruned);
  }
  return pruned;
};

export const getTrainingPreviewImageUrl = (
  data?: AcademyTrainingDetail | Record<string, unknown> | null
): string | null => {
  if (!data) return null;
  const record = data as Record<string, unknown>;
  const image =
    record.PreviewImageUrl ||
    record.PreviewImage ||
    record.ImageUrl ||
    record.Image ||
    record.previewImageUrl ||
    record.image;
  return resolveImageUrl(typeof image === 'string' ? image : null);
};

export const getCachedTrainingPreviewImage = (trainingId?: string | number | null): string | null => {
  if (!trainingId) return null;
  const entry = getValidCache()[String(trainingId)];
  return entry?.imageUrl || null;
};

export const getCachedTrainingPreviewImages = (
  trainingIds?: Array<string | number> | null
): Record<string, string> => {
  const cache = getValidCache();
  return (trainingIds || []).reduce<Record<string, string>>((images, trainingId) => {
    const imageUrl = cache[String(trainingId)]?.imageUrl;
    if (imageUrl) {
      images[String(trainingId)] = imageUrl;
    }
    return images;
  }, {});
};

export const setCachedTrainingPreviewImage = (
  trainingId?: string | number | null,
  imageUrl?: string | null
): void => {
  if (!trainingId || !imageUrl) return;
  const cache = getValidCache();
  cache[String(trainingId)] = {
    imageUrl,
    expiresAt: Date.now() + CACHE_TTL_MS
  };
  writeCache(cache);
};

export const fetchTrainingPreviewImage = async (trainingId?: string | number | null): Promise<string | null> => {
  if (!trainingId) return null;

  const cachedImageUrl = getCachedTrainingPreviewImage(trainingId);
  if (cachedImageUrl) return cachedImageUrl;

  const response = await axios.get<AcademyTrainingDetail>(`${ENDPOINTS.GET_EVENTS_TRAININGS_ENDPOINT}/${trainingId}`);
  const imageUrl = getTrainingPreviewImageUrl(response?.data);
  if (imageUrl) {
    setCachedTrainingPreviewImage(trainingId, imageUrl);
  }
  return imageUrl;
};
